import { useMemo, useState } from 'react';
import { useRouteRef } from '@backstage/frontend-plugin-api';
import {
  Flex,
  Skeleton,
  Text,
  Table,
  useTable,
  CellText,
  Cell,
  type ColumnConfig,
  type SortDescriptor,
} from '@backstage/ui';
import type { TrackLevel } from '@internal/backstage-plugin-pulse-common';
import {
  useTrackChecks,
  type TrackDetailsData,
  type TrackCheckAggregate,
} from '../../hooks';
import { checksDetailsSubRouteRef } from '../../routes';
import { CompliancePercentageBar } from './compliance-indicators';
import { LEVEL_COLORS } from './track-level-colors';
import { getLevelImportanceLabel } from './track-utils';
import { withTeamsParam } from '../with-teams-param';
import { sortCollator } from '../config';

type CheckRow = TrackCheckAggregate & { id: string };

const compareNumbers = (left: number, right: number): number => left - right;

export const getCheckSortDescriptor = (
  currentSort: SortDescriptor,
  requestedSort: SortDescriptor,
): SortDescriptor => {
  if (currentSort.column === requestedSort.column) {
    return {
      column: currentSort.column,
      direction:
        currentSort.direction === 'ascending' ? 'descending' : 'ascending',
    };
  }

  return {
    column: requestedSort.column,
    direction: 'descending',
  };
};

export const sortCheckRows = (
  data: CheckRow[],
  sort: SortDescriptor,
): CheckRow[] => {
  const direction = sort.direction === 'descending' ? -1 : 1;
  const sortColumn = String(sort.column);

  return [...data].sort((left, right) => {
    let comparison: number;

    switch (sortColumn) {
      case 'checks':
        comparison =
          compareNumbers(left.passedEntities, right.passedEntities) ||
          compareNumbers(left.totalEntities, right.totalEntities);
        break;
      case 'compliance':
        comparison = compareNumbers(left.percentage, right.percentage);
        break;
      case 'name':
      default:
        comparison = sortCollator.compare(left.name.trim(), right.name.trim());
        break;
    }

    if (comparison !== 0) {
      return comparison * direction;
    }

    return sortCollator.compare(left.id, right.id);
  });
};

const createCheckColumns = (
  getCheckDetailsPath: ((params: { id: string }) => string) | undefined,
  selectedGroups: string[],
): ColumnConfig<CheckRow>[] => [
  {
    id: 'name',
    label: 'Name',
    isRowHeader: true,
    defaultWidth: '40fr',
    isSortable: true,
    cell: item => (
      <CellText
        title={item.name}
        href={
          getCheckDetailsPath
            ? withTeamsParam(
                getCheckDetailsPath({ id: item.checkId }),
                selectedGroups,
              )
            : undefined
        }
      />
    ),
  },
  {
    id: 'checks',
    label: 'Checks',
    defaultWidth: '25fr',
    isSortable: true,
    cell: item => (
      <CellText
        title={`${item.passedEntities} of ${item.totalEntities} entities passing`}
        color="secondary"
      />
    ),
  },
  {
    id: 'compliance',
    label: 'Compliance',
    defaultWidth: '35fr',
    isSortable: true,
    cell: item => (
      <Cell>
        <CompliancePercentageBar percentage={item.percentage} />
      </Cell>
    ),
  },
];

const LevelChecksTable = ({
  level,
  aggregates,
  levelIndex,
  selectedGroups,
}: {
  level: TrackLevel;
  aggregates: Map<string, TrackCheckAggregate>;
  levelIndex: number;
  selectedGroups: string[];
}) => {
  const getCheckDetailsPath = useRouteRef(checksDetailsSubRouteRef);
  const columns = createCheckColumns(getCheckDetailsPath, selectedGroups);
  const [sort, setSort] = useState<SortDescriptor>({
    column: 'name',
    direction: 'ascending',
  });

  const rows: CheckRow[] = level.checkIds.map((checkId, index) => {
    const aggregate = aggregates.get(checkId) ?? {
      checkId,
      name: checkId,
      passedEntities: 0,
      totalEntities: 0,
      percentage: 0,
    };
    return { ...aggregate, id: `${checkId}-${index}` };
  });

  const { tableProps } = useTable<CheckRow, string[]>({
    mode: 'complete',
    data: rows,
    sort,
    onSortChange: requestedSort =>
      setSort(currentSort =>
        getCheckSortDescriptor(currentSort, requestedSort),
      ),
    sortFn: sortCheckRows,
    paginationOptions: { type: 'none' },
  });

  return (
    <Flex direction="column" gap="2">
      <Flex align="center" gap="2">
        <span
          style={{
            display: 'inline-block',
            width: '10px',
            height: '10px',
            borderRadius: 'var(--bui-radius-2)',
            backgroundColor: LEVEL_COLORS[levelIndex % LEVEL_COLORS.length],
            flexShrink: 0,
          }}
        />
        <Text variant="title-x-small" weight="bold">
          {getLevelImportanceLabel(level.importance)} checks
        </Text>
      </Flex>
      {level.description ? (
        <Text color="secondary">{level.description}</Text>
      ) : null}
      <Table
        columnConfig={columns}
        {...tableProps}
        pagination={{ type: 'none' }}
        emptyState="No checks in this level"
      />
    </Flex>
  );
};

/**
 * "Checks By Levels" tab: one table per track level (in level order) showing
 * each check's aggregated compliance across all entities the check applies to.
 */
export const ChecksByLevels = ({
  details,
  entityRefs,
  selectedGroups = [],
}: {
  details: TrackDetailsData;
  entityRefs?: string[];
  selectedGroups?: string[];
}) => {
  const uniqueCheckIds = useMemo(() => {
    const ids = new Set<string>();
    for (const level of details.track.levels) {
      for (const checkId of level.checkIds) {
        ids.add(checkId);
      }
    }
    return [...ids];
  }, [details.track.levels]);

  const {
    value: aggregates,
    loading,
    error,
  } = useTrackChecks(uniqueCheckIds, entityRefs);

  const sortedLevels = useMemo(
    () => [...details.track.levels].sort((a, b) => a.ordinal - b.ordinal),
    [details.track.levels],
  );

  if (loading) {
    return (
      <Flex direction="column" gap="2">
        <Skeleton width={160} height="var(--bui-space-8)" />
        <Skeleton width="100%" height="var(--bui-space-6)" />
        {Array.from({ length: 5 }, (_, index) => (
          <Skeleton
            key={`row-${index}`}
            width="100%"
            height="var(--bui-space-8)"
          />
        ))}
      </Flex>
    );
  }

  if (error) {
    return <Text color="danger">{error.message}</Text>;
  }

  const aggregatesMap = aggregates ?? new Map<string, TrackCheckAggregate>();

  return (
    <Flex direction="column" gap="6">
      {sortedLevels.map((level, index) => (
        <LevelChecksTable
          key={level.ordinal}
          level={level}
          aggregates={aggregatesMap}
          levelIndex={index}
          selectedGroups={selectedGroups}
        />
      ))}
    </Flex>
  );
};
